// State Manager
// Centralized reactive state management

import { FILTER_TYPES } from '../config/constants.js';

/**
 * StateManager - Reactive state management with subscriptions
 */
class StateManager {
    constructor() {
        this.state = {
            // Current customer ID (from localStorage or URL)
            currentCustomerId: localStorage.getItem('customerId'),

            // Current authenticated user
            currentUser: null,

            // All customers from Firestore
            allCustomers: [],

            // All sellers data from Firestore
            sellersData: {},

            // Current seller state
            isSellerPaused: false,
            isShiftActive: false,

            // Staff view filter (waiting or standby)
            listFilter: FILTER_TYPES.WAITING,

            // Configuration
            avgServiceTime: 12 // Default, updated from Firestore
        };

        // Subscriptions for reactive updates
        this.listeners = new Map();
    }

    /**
     * Subscribe to state changes for a specific key
     * @param {string} key - The state key to watch
     * @param {function} callback - Function to call when state changes
     * @returns {function} Unsubscribe function
     */
    subscribe(key, callback) {
        if (!this.listeners.has(key)) {
            this.listeners.set(key, []);
        }

        this.listeners.get(key).push(callback);

        // Return unsubscribe function
        return () => {
            const callbacks = this.listeners.get(key);
            const index = callbacks.indexOf(callback);
            if (index > -1) {
                callbacks.splice(index, 1);
            }
        };
    }

    /**
     * Set state and notify subscribers
     * @param {string} key - The state key to update
     * @param {any} value - The new value
     */
    setState(key, value) {
        const oldValue = this.state[key];

        // Only update and notify if value actually changed
        if (oldValue !== value) {
            this.state[key] = value;

            // Notify all subscribers
            if (this.listeners.has(key)) {
                this.listeners.get(key).forEach(callback => {
                    callback(value, oldValue);
                });
            }
        }
    }

    /**
     * Get current state value
     * @param {string} key - The state key to get
     * @returns {any} The current value
     */
    getState(key) {
        return this.state[key];
    }

    /**
     * Get all state (for debugging)
     * @returns {object} The entire state object
     */
    getAllState() {
        return { ...this.state };
    }

    /**
     * Set current customer ID and persist to localStorage
     * @param {string} customerId - The customer ID
     */
    setCurrentCustomerId(customerId) {
        if (customerId) {
            localStorage.setItem('customerId', customerId);
        } else {
            localStorage.removeItem('customerId');
        }
        this.setState('currentCustomerId', customerId);
    }

    /**
     * Clear current customer ID
     */
    clearCurrentCustomerId() {
        localStorage.removeItem('customerId');
        this.setState('currentCustomerId', null);
    }

    /**
     * Set all customers (from Firestore listener)
     * @param {array} customers - Array of customer objects
     */
    setAllCustomers(customers) {
        this.setState('allCustomers', customers);
    }

    /**
     * Set sellers data (from Firestore listener)
     * @param {object} sellersData - Object with seller data keyed by UID
     */
    setSellersData(sellersData) {
        this.setState('sellersData', sellersData);
    }

    /**
     * Set current user (from auth state)
     * @param {object} user - Firebase user object
     */
    setCurrentUser(user) {
        this.setState('currentUser', user);
    }

    /**
     * Set seller shift status
     * @param {boolean} isActive - Whether shift is active
     */
    setShiftActive(isActive) {
        this.setState('isShiftActive', isActive);
    }

    /**
     * Set seller pause status
     * @param {boolean} isPaused - Whether seller is paused
     */
    setSellerPaused(isPaused) {
        this.setState('isSellerPaused', isPaused);
    }

    /**
     * Set list filter (waiting or standby)
     * @param {string} filter - Filter type from FILTER_TYPES
     */
    setListFilter(filter) {
        this.setState('listFilter', filter);
    }

    /**
     * Set average service time from config
     * @param {number} avgTime - Average service time in minutes
     */
    setAvgServiceTime(avgTime) {
        this.setState('avgServiceTime', avgTime);
    }
}

// Create and export singleton instance
const stateManager = new StateManager();

export default stateManager;
